/**
 * MatchHomePage — landing page for the /match section.
 *
 * Mobile-first single column with a single desktop 2-column split at `lg`.
 * Surfaces the three things the owner wants at a glance:
 *   1. A large CTA to open Quick Entry (the thing they'll do daily).
 *   2. Last 10 match entries with a View deep-link into each.
 *   3. Top 5 match accounts by |balance| with a deep-link into each ledger.
 *
 * Data pulled through the TanStack Query wrappers so invalidation from the
 * entry page picks up here automatically.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  FolderKanban,
  Plus,
  ReceiptText,
  Zap,
} from 'lucide-react';
import {
  match,
  type MatchAccount,
  type MatchEntry,
} from '../api';
import { MatchShell } from './MatchShell';
import {
  Button,
  Card,
  EmptyState,
  Pill,
  Spinner,
} from '../ui/primitives';

/**
 * Format a canonical Decimal INR string for display.
 * The backend stores exact INR; here we pretty-print with thousand separators
 * while preserving the sign. No rounding — just a thin formatter.
 */
function formatInr(raw: string): string {
  if (!raw) return '0';
  const neg = raw.startsWith('-');
  const abs = neg ? raw.slice(1) : raw;
  const [intPart, fracPart] = abs.split('.');
  const withCommas = (intPart ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const formatted = fracPart ? `${withCommas}.${fracPart}` : withCommas;
  return `${neg ? '-' : ''}₹${formatted}`;
}

/** Absolute value of a decimal string for sorting by magnitude. */
function absDecimal(raw: string): number {
  if (!raw) return 0;
  const n = Number(raw.startsWith('-') ? raw.slice(1) : raw);
  return Number.isFinite(n) ? n : 0;
}

export function MatchHomePage() {
  const nav = useNavigate();

  const accountsQ = useQuery<MatchAccount[]>({
    queryKey: ['match-accounts-active'],
    queryFn: () => match.listAccounts({ status: 'active' }),
  });

  const balancesQ = useQuery<Array<{ matchAccountId: number; balance: string }>>(
    {
      queryKey: ['match-balances'],
      queryFn: () => match.listBalances(),
    },
  );

  const recentEntriesQ = useQuery<MatchEntry[]>({
    queryKey: ['match-entries-recent'],
    queryFn: () => match.listEntries({ limit: 10 }),
  });

  const accountsById = useMemo(
    () => new Map((accountsQ.data ?? []).map((a) => [a.id, a])),
    [accountsQ.data],
  );

  const topAccounts = useMemo(() => {
    const accounts = accountsQ.data ?? [];
    const balances = balancesQ.data ?? [];
    const balById = new Map(balances.map((b) => [b.matchAccountId, b.balance]));
    return accounts
      .filter((a) => a.status === 'active')
      .map((a) => ({ account: a, balance: balById.get(a.id) ?? '0' }))
      .sort((x, y) => absDecimal(y.balance) - absDecimal(x.balance))
      .slice(0, 5);
  }, [accountsQ.data, balancesQ.data]);

  const hasAccounts = (accountsQ.data ?? []).length > 0;

  return (
    <MatchShell>
      <div className="space-y-4">
        {/* Hero CTA card */}
        <Card className="!p-0 overflow-hidden border-brand/20 bg-gradient-to-br from-brand/5 via-surface to-surface">
          <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-brand">
                <Zap className="h-4 w-4" strokeWidth={2} />
                <span className="text-caption font-semibold uppercase tracking-wider">
                  Match
                </span>
              </div>
              <h1 className="mt-1 text-xl font-semibold text-ink sm:text-2xl">
                New Entry
              </h1>
              <p className="mt-1 text-small text-ink-muted">
                Record a signed amount against a match account — split
                distribution applies automatically.
              </p>
            </div>
            <Button
              size="md"
              className="w-full sm:w-auto"
              onClick={() => nav('/match/entry')}
              disabled={!hasAccounts && accountsQ.isFetched}
            >
              <Plus className="h-4 w-4" strokeWidth={2} />
              <span>Quick Entry</span>
              <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </Button>
          </div>
          {accountsQ.isFetched && !hasAccounts && (
            <div className="border-t border-brand/10 bg-surface-raised px-4 py-3 text-small text-ink-muted sm:px-6">
              You don&apos;t have any match accounts yet.{' '}
              <Link
                to="/match/accounts"
                className="font-medium text-brand hover:underline"
              >
                Create your first one →
              </Link>
            </div>
          )}
        </Card>

        {/* Two-column content grid */}
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Recent activity */}
          <section className="space-y-2">
            <header className="flex items-center justify-between">
              <h2 className="text-h2 font-semibold text-ink">
                Recent activity
              </h2>
              <Link
                to="/match/accounts"
                className="text-small text-ink-muted hover:text-ink hover:underline"
              >
                All accounts →
              </Link>
            </header>
            <Card>
              {recentEntriesQ.isLoading ? (
                <div className="flex items-center gap-2 text-small text-ink-muted">
                  <Spinner /> Loading recent entries…
                </div>
              ) : (recentEntriesQ.data ?? []).length === 0 ? (
                <EmptyState
                  icon={ReceiptText}
                  title="No entries yet"
                  description="Submit your first Quick Entry to see activity here."
                />
              ) : (
                <ul className="divide-y divide-border">
                  {(recentEntriesQ.data ?? []).map((e) => {
                    const acct = accountsById.get(e.sourceMatchAccountId);
                    return (
                      <li
                        key={e.id}
                        className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-small font-medium text-ink">
                              {acct?.name ?? `#${e.sourceMatchAccountId}`}
                            </span>
                            {e.voidedAt && (
                              <Pill tone="red">Voided</Pill>
                            )}
                            {e.hadOverride && !e.voidedAt && (
                              <Pill tone="indigo">Override</Pill>
                            )}
                          </div>
                          <div className="mt-0.5 flex items-center gap-2 text-caption text-ink-muted">
                            <span>{e.effectiveDate}</span>
                            <span
                              className={
                                e.amount.startsWith('-')
                                  ? 'font-mono text-neg'
                                  : 'font-mono text-pos'
                              }
                            >
                              {formatInr(e.amount)}
                            </span>
                          </div>
                        </div>
                        <Link
                          to={`/match/entries/${e.id}`}
                          className="shrink-0 text-caption font-medium text-brand hover:underline"
                        >
                          View
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </section>

          {/* Top accounts by |balance| */}
          <section className="space-y-2">
            <header className="flex items-center justify-between">
              <h2 className="text-h2 font-semibold text-ink">
                Top accounts
              </h2>
              <Link
                to="/match/accounts"
                className="text-small text-ink-muted hover:text-ink hover:underline"
              >
                Manage →
              </Link>
            </header>
            <Card>
              {accountsQ.isLoading || balancesQ.isLoading ? (
                <div className="flex items-center gap-2 text-small text-ink-muted">
                  <Spinner /> Loading balances…
                </div>
              ) : topAccounts.length === 0 ? (
                <EmptyState
                  icon={BarChart3}
                  title="No active accounts"
                  description="Create match accounts to see their balances here."
                  action={
                    <Link
                      to="/match/accounts"
                      className="inline-flex items-center gap-1 text-small font-medium text-brand hover:underline"
                    >
                      <Plus className="h-4 w-4" strokeWidth={2} />
                      Manage accounts
                    </Link>
                  }
                />
              ) : (
                <ul className="divide-y divide-border">
                  {topAccounts.map(({ account, balance }) => {
                    const neg = balance.startsWith('-');
                    return (
                      <li
                        key={account.id}
                        className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"
                      >
                        <Link
                          to={`/match/accounts/${account.id}/ledger`}
                          className="min-w-0 flex-1 truncate text-small font-medium text-ink hover:text-brand hover:underline"
                        >
                          {account.name}
                        </Link>
                        <span
                          className={[
                            'shrink-0 font-mono text-small',
                            neg ? 'text-neg' : 'text-pos',
                          ].join(' ')}
                        >
                          {formatInr(balance)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>

            {/* Quick links */}
            <Card>
              <div className="flex items-center gap-3">
                <FolderKanban className="h-5 w-5 shrink-0 text-ink-muted" strokeWidth={1.75} />
                <div className="min-w-0 flex-1">
                  <div className="text-small font-medium text-ink">
                    Manage accounts
                  </div>
                  <div className="text-caption text-ink-muted">
                    Configure splits, archive unused accounts.
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => nav('/match/accounts')}
                >
                  Open
                </Button>
              </div>
            </Card>
          </section>
        </div>
      </div>
    </MatchShell>
  );
}
